import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/store';
import {
    Clock,
    Code2,
    Coins,
    Database,
    FileCode,
    Globe,
    Image,
    Layers,
    Layout,
    Loader2,
    Lock,
    Repeat,
    Search,
    Shield,
    Sparkles,
    Star,
    TrendingUp,
    Users,
    Wallet,
    X,
} from 'lucide-react';
import type { ElementType } from 'react';
import { createElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TemplateCard {
  id: string;
  name: string;
  description: string;
  category: 'smart-contract' | 'dapp' | 'fullstack' | 'defi' | 'governance';
  icon: ElementType;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  stars: number;
  featured?: boolean;
}

const templates: TemplateCard[] = [
  // — Smart Contracts —
  {
    id: 'blank-solidity',
    name: 'Blank Solidity',
    description:
      'Start with an empty Solidity contract. Perfect for learning or building from scratch.',
    category: 'smart-contract',
    icon: FileCode,
    tags: ['solidity', 'contract', 'blank'],
    difficulty: 'beginner',
    stars: 1240,
  },
  {
    id: 'erc20-token',
    name: 'ERC-20 Token',
    description: 'Standard ERC-20 token with mint, burn, and transfer. OpenZeppelin-based.',
    category: 'smart-contract',
    icon: Coins,
    tags: ['solidity', 'erc20', 'token', 'defi'],
    difficulty: 'beginner',
    stars: 3820,
    featured: true,
  },
  {
    id: 'erc721-nft',
    name: 'ERC-721 NFT',
    description: 'NFT collection contract with metadata, minting, and royalty support.',
    category: 'smart-contract',
    icon: Image,
    tags: ['solidity', 'erc721', 'nft', 'collectible'],
    difficulty: 'intermediate',
    stars: 2950,
    featured: true,
  },
  {
    id: 'erc1155-multi',
    name: 'ERC-1155 Multi-Token',
    description: 'Multi-token standard for fungible and non-fungible tokens in a single contract.',
    category: 'smart-contract',
    icon: Layers,
    tags: ['solidity', 'erc1155', 'multi-token', 'gaming'],
    difficulty: 'intermediate',
    stars: 1680,
  },
  {
    id: 'upgradeable-proxy',
    name: 'Upgradeable Proxy',
    description: 'UUPS or Transparent proxy pattern for upgradeable smart contracts. OpenZeppelin.',
    category: 'smart-contract',
    icon: Repeat,
    tags: ['solidity', 'proxy', 'upgradeable', 'uups'],
    difficulty: 'advanced',
    stars: 2210,
  },
  {
    id: 'access-control',
    name: 'Access Control',
    description: 'Role-based access control contract with admin roles and multi-sig patterns.',
    category: 'smart-contract',
    icon: Lock,
    tags: ['solidity', 'access-control', 'roles', 'security'],
    difficulty: 'intermediate',
    stars: 1450,
  },

  // — DeFi —
  {
    id: 'staking-pool',
    name: 'Staking Pool',
    description:
      'Token staking contract with rewards distribution, lock periods, and yield calculation.',
    category: 'defi',
    icon: TrendingUp,
    tags: ['solidity', 'staking', 'defi', 'yield'],
    difficulty: 'advanced',
    stars: 3100,
    featured: true,
  },
  {
    id: 'token-swap',
    name: 'Token Swap',
    description: 'Simple AMM-style token swap with liquidity pools and fee collection.',
    category: 'defi',
    icon: Repeat,
    tags: ['solidity', 'amm', 'swap', 'defi', 'liquidity'],
    difficulty: 'advanced',
    stars: 2740,
  },
  {
    id: 'token-vesting',
    name: 'Token Vesting',
    description:
      'Vesting schedule contract with cliff periods, linear unlock, and revocable grants.',
    category: 'defi',
    icon: Clock,
    tags: ['solidity', 'vesting', 'tokenomics', 'defi'],
    difficulty: 'intermediate',
    stars: 1890,
  },

  // — Governance —
  {
    id: 'dao-governor',
    name: 'DAO Governor',
    description:
      'On-chain governance with proposal creation, voting, and execution. OpenZeppelin Governor.',
    category: 'governance',
    icon: Users,
    tags: ['solidity', 'dao', 'governance', 'voting'],
    difficulty: 'advanced',
    stars: 2650,
  },
  {
    id: 'multi-sig-wallet',
    name: 'Multi-Sig Wallet',
    description: 'Multi-signature wallet requiring N-of-M signatures for transaction execution.',
    category: 'governance',
    icon: Shield,
    tags: ['solidity', 'multisig', 'wallet', 'security'],
    difficulty: 'advanced',
    stars: 2230,
  },

  // — dApps —
  {
    id: 'nextjs-dapp',
    name: 'Next.js dApp',
    description: 'Next.js frontend with ethers.js, wallet connection, and your RPC endpoint.',
    category: 'dapp',
    icon: Layout,
    tags: ['nextjs', 'ethers', 'react', 'dapp', 'frontend'],
    difficulty: 'intermediate',
    stars: 4120,
    featured: true,
  },
  {
    id: 'react-dapp',
    name: 'React dApp',
    description: 'React + Vite frontend with ethers.js and Web3 wallet integration.',
    category: 'dapp',
    icon: Code2,
    tags: ['react', 'vite', 'ethers', 'dapp', 'frontend'],
    difficulty: 'beginner',
    stars: 3560,
  },
  {
    id: 'wallet-connect',
    name: 'WalletConnect dApp',
    description: 'dApp with WalletConnect v2, multi-wallet support, and chain switching.',
    category: 'dapp',
    icon: Wallet,
    tags: ['walletconnect', 'react', 'dapp', 'multi-wallet'],
    difficulty: 'intermediate',
    stars: 1970,
  },

  // — Full Stack —
  {
    id: 'fullstack',
    name: 'Full Stack',
    description:
      'Complete stack: Hardhat for contracts, React frontend, and ethers.js integration.',
    category: 'fullstack',
    icon: Layers,
    tags: ['hardhat', 'react', 'ethers', 'fullstack', 'solidity'],
    difficulty: 'intermediate',
    stars: 5230,
    featured: true,
  },
  {
    id: 'subgraph-indexer',
    name: 'Subgraph + Frontend',
    description: 'Full stack with The Graph subgraph for indexed blockchain data queries.',
    category: 'fullstack',
    icon: Database,
    tags: ['subgraph', 'thegraph', 'react', 'indexer', 'graphql'],
    difficulty: 'advanced',
    stars: 1350,
  },
  {
    id: 'cross-chain',
    name: 'Cross-Chain Bridge',
    description: 'Multi-chain dApp template with contracts deployed across multiple chains.',
    category: 'fullstack',
    icon: Globe,
    tags: ['cross-chain', 'bridge', 'multi-chain', 'solidity'],
    difficulty: 'advanced',
    stars: 1120,
  },
];

const categoryLabels: Record<string, string> = {
  'smart-contract': 'Smart Contract',
  defi: 'DeFi',
  governance: 'Governance',
  dapp: 'dApp',
  fullstack: 'Full Stack',
};

const categoryColors: Record<string, string> = {
  'smart-contract': 'bg-purple-600/20 text-purple-400 border-purple-500/30',
  defi: 'bg-yellow-600/20 text-yellow-400 border-yellow-500/30',
  governance: 'bg-cyan-600/20 text-cyan-400 border-cyan-500/30',
  dapp: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
  fullstack: 'bg-green-600/20 text-green-400 border-green-500/30',
};

const difficultyColors: Record<string, string> = {
  beginner: 'text-green-400',
  intermediate: 'text-yellow-400',
  advanced: 'text-red-400',
};

function formatStars(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export default function TemplateSelectPage() {
  const navigate = useNavigate();
  const { createWorkspace } = useWorkspaceStore();

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateCard | null>(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch =
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.tags.some((tag) => tag.includes(search.toLowerCase()));
      const matchesCategory = !selectedCategory || t.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory]);

  const handleSelectTemplate = (template: TemplateCard) => {
    setSelectedTemplate(template);
    setWorkspaceName(`my-${template.id}`);
    setShowModal(true);
  };

  const handleCreate = async () => {
    if (!selectedTemplate || !workspaceName.trim()) return;

    setIsCreating(true);
    try {
      const ws = await createWorkspace(workspaceName.trim(), selectedTemplate.id, 'default');
      navigate(`/ide/${ws.id}`);
    } catch {
      // Error handled by store
    } finally {
      setIsCreating(false);
    }
  };

  const categories = ['smart-contract', 'defi', 'governance', 'dapp', 'fullstack'];

  const featured = useMemo(() => templates.filter((t) => t.featured), []);

  return (
    <div className="min-h-screen bg-[#1e1e1e] text-white">
      {/* Header */}
      <header className="border-b border-[#333] bg-[#252526]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">New Workspace</h1>
            <p className="text-sm text-[#858585] mt-0.5">Choose a template to get started</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-[#858585] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Search + Filters */}
        <div className="flex items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#858585]" />
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#3c3c3c] border border-[#555] rounded text-sm text-white placeholder-[#858585] focus:outline-none focus:border-[#0e639c] transition-colors"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn(
                'px-3 py-1.5 rounded text-xs border transition-colors',
                !selectedCategory
                  ? 'bg-[#0e639c] border-[#0e639c] text-white'
                  : 'bg-transparent border-[#555] text-[#858585] hover:text-white hover:border-[#888]',
              )}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                className={cn(
                  'px-3 py-1.5 rounded text-xs border transition-colors',
                  selectedCategory === cat
                    ? 'bg-[#0e639c] border-[#0e639c] text-white'
                    : 'bg-transparent border-[#555] text-[#858585] hover:text-white hover:border-[#888]',
                )}
              >
                {categoryLabels[cat]}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Templates */}
        {!search && !selectedCategory && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <h2 className="text-sm font-semibold text-[#cccccc] uppercase tracking-wider">
                Featured
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="text-left bg-[#252526] border border-yellow-500/30 rounded-lg p-5 hover:border-yellow-400/60 transition-colors group relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 px-2 py-0.5 bg-yellow-600/20 text-yellow-400 text-[10px] font-medium rounded-bl">
                      <Star className="w-3 h-3 inline mr-0.5" />
                      Featured
                    </div>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-[#333] rounded-lg group-hover:bg-yellow-500/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#cccccc] group-hover:text-yellow-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-white">{template.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={cn(
                              'text-[10px] font-medium',
                              difficultyColors[template.difficulty],
                            )}
                          >
                            {template.difficulty}
                          </span>
                          <span className="text-[10px] text-[#858585] flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5" /> {formatStars(template.stars)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-[#858585] mb-3 leading-relaxed">
                      {template.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {template.tags.slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-[#333] text-[#858585] rounded text-[10px]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* All Templates */}
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-[#858585]" />
          <h2 className="text-sm font-semibold text-[#cccccc] uppercase tracking-wider">
            {selectedCategory ? categoryLabels[selectedCategory] : 'All Templates'}
          </h2>
          <span className="text-xs text-[#858585]">({filtered.length})</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((template) => {
            const Icon = template.icon;
            return (
              <button
                key={template.id}
                onClick={() => handleSelectTemplate(template)}
                className="text-left bg-[#252526] border border-[#333] rounded-lg p-5 hover:border-[#0e639c] transition-colors group"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-[#333] rounded-lg group-hover:bg-[#0e639c]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#cccccc] group-hover:text-[#4fc1ff]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-white">{template.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={cn(
                          'inline-block px-2 py-0.5 text-[10px] font-medium rounded border',
                          categoryColors[template.category],
                        )}
                      >
                        {categoryLabels[template.category]}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] font-medium',
                          difficultyColors[template.difficulty],
                        )}
                      >
                        {template.difficulty}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-[#858585] mb-3 leading-relaxed">
                  {template.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {template.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-[#333] text-[#858585] rounded text-[10px]"
                      >
                        {tag}
                      </span>
                    ))}
                    {template.tags.length > 3 && (
                      <span className="px-2 py-0.5 text-[#858585] text-[10px]">
                        +{template.tags.length - 3}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-[#858585] flex items-center gap-0.5 shrink-0">
                    <Star className="w-2.5 h-2.5" /> {formatStars(template.stars)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 text-[#858585] mx-auto mb-3" />
            <p className="text-[#858585]">No templates match your search.</p>
          </div>
        )}
      </main>

      {/* Create workspace modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#252526] border border-[#555] rounded-lg w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-white">Create Workspace</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#858585] hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-2 mb-4 p-3 bg-[#1e1e1e] rounded">
                {createElement(selectedTemplate.icon, {
                  className: 'w-5 h-5 text-[#4fc1ff]',
                })}
                <div>
                  <p className="text-sm font-medium text-white">{selectedTemplate.name}</p>
                  <p className="text-xs text-[#858585]">
                    {categoryLabels[selectedTemplate.category]}
                  </p>
                </div>
              </div>

              <label className="block text-sm text-[#cccccc] mb-2">Workspace Name</label>
              <input
                type="text"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="my-project"
                className="w-full px-3 py-2 bg-[#3c3c3c] border border-[#555] rounded text-sm text-white placeholder-[#858585] focus:outline-none focus:border-[#0e639c] transition-colors"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-[#cccccc] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!workspaceName.trim() || isCreating}
                className="inline-flex items-center gap-2 px-5 py-2 bg-[#0e639c] hover:bg-[#1177bb] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
              >
                {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
